import {
	originType,
	tenantType,
	userType,
} from '@atlassiansox/analytics-web-client/analytics-web-types';
import AnalyticsWebClient from '@atlassiansox/analytics-web-client/client';

import { setAvailableSitesRequestTimer } from '@atlassian/twg-microsite-common/site-state';

import { getConfig } from '../../config';
import { withRequestTiming } from './withRequestTiming';

const product = 'teamworkGraph';

const getIdentityEnv = (env: string): 'dev' | 'staging' | 'prod' => {
	switch (env) {
		case 'production':
			return 'prod';
		case 'staging':
			return 'staging';
		case 'development':
		case 'local':
		default:
			return 'dev';
	}
};

const getGasApiHost = (env: ReturnType<typeof getConfig>['env']): string => {
	switch (env) {
		case 'production':
			return `${window.location.host}/gateway/api/gasv3/api/v1`;
		case 'staging':
		case 'development':
		case 'local':
		default:
			return 'team.stg.atlassian.com/gateway/api/gasv3/api/v1';
	}
};

let _client: AnalyticsWebClient | null = null;

/**
 * Initialise (or return) the singleton AnalyticsWebClient. Safe to call
 * multiple times — subsequent calls return the existing instance. The first
 * call also times the `availableSitesAtom` request through this client.
 */
export const initAnalyticsClient = (): AnalyticsWebClient => {
	if (_client) {
		return _client;
	}
	const { env, isLocalDev } = getConfig();
	_client = new AnalyticsWebClient(
		{
			env: getIdentityEnv(env),
			origin: originType.WEB,
			product,
			subproduct: 'twg-microsite-experience',
		},
		{
			apiHost: getGasApiHost(env),
			apiHostProtocol: isLocalDev ? 'http' : 'https',
			disableCookiePersistence: true,
			// Default is 500ms; reduce to 100ms to increase reliability of
			// events fired immediately before navigation/redirect (e.g. app
			// switcher, sign-out, cross-origin links). Mirrors WAC's setting
			// in `wac/services/wac-web/src/analytics/wac-analytics/get-analytics-web-client.tsx`.
			flushWaitInterval: 100,
		},
	);
	setAvailableSitesRequestTimer((execute) =>
		withRequestTiming(
			'operational.availableProductsRequest.completed.twgMicrositeAvailableProducts',
			execute,
		),
	);
	return _client;
};

/**
 * Returns the singleton AnalyticsWebClient, throwing if not yet initialised.
 *
 * INTERNAL — do not call from React components or hooks. React code should
 * use `useAnalyticsEvents()` (codegen-typed hook) so events flow through
 * @atlaskit/analytics-next listeners with the correct channel + source.
 *
 * The only legitimate caller is non-React code (Jotai async atoms, request
 * timing wraps, etc.) that needs to send analytics outside the React tree.
 */
export const getAnalyticsClient = (): AnalyticsWebClient => {
	if (!_client) {
		throw new Error('AnalyticsWebClient not initialised — call initAnalyticsClient() first.');
	}
	return _client;
};

/**
 * Wires the resolved user identity into the GAS client so all downstream
 * events carry the correct AAID / tenant via the analytics-web-client
 * envelope (no need to put them in event attributes — see GASv3 docs).
 *
 * INTERNAL — only called from UserProvider after auth resolves. Not part of
 * the package's public API. Idempotent / best-effort: silently no-ops if
 * the client has not yet been initialised, and ignores empty AAID values.
 * Callers may pass either `orgId` (preferred) or `cloudId` for tenant info;
 * if both are provided `orgId` wins.
 */
export const setMicrositeUser = (
	aaid: string,
	orgId?: string | null,
	cloudId?: string | null,
): void => {
	if (!_client) {
		return;
	}
	if (aaid) {
		_client.setUserInfo(userType.ATLASSIAN_ACCOUNT, aaid);
	}
	if (orgId) {
		_client.setTenantInfo(tenantType.ORG_ID, orgId);
	} else if (cloudId) {
		_client.setTenantInfo(tenantType.CLOUD_ID, cloudId);
	}
};

/** Test-only: reset the singleton. Do not call from app code. */
export const __resetAnalyticsClientForTests = (): void => {
	_client = null;
};
